// Port of SuiteHistory/TextDiff.swift: a prose-aware diff (cross-platform spec §6.3, level 1). Sentences first, so a
// moved sentence reads as a move rather than a deletion plus an insertion; then words inside sentences rewritten in
// place. Both sides are the dialect's plain text (`ProseMarkdown.plainText`), so markup alone is never a change.
//
// Approximate: sentences are text/sentences.ts's (`Intl.Segmenter` joined after titles and initials) where the Mac uses
// `NLTokenizer`; the two agree on ordinary prose and can differ on quotations and rarer abbreviations, which moves a word
// diff's boundaries but not what's added or cut.
import { plainText } from '../proseMarkdown.ts';
import { sentenceTexts } from '../text/sentences.ts';
import { difference, inferringMoves } from './collectionDifference.ts';

export type Segment =
  | { type: 'equal'; text: string }
  | { type: 'inserted'; text: string }
  | { type: 'deleted'; text: string }
  /** A sentence that moved away from here (`toIndex`: its position among the new text's sentences). */
  | { type: 'movedAway'; text: string; toIndex: number }
  /** A sentence that moved here (`fromIndex`: its position among the old text's sentences). */
  | { type: 'movedHere'; text: string; fromIndex: number };

export interface TextDiffResult {
  segments: Segment[];
  wordsAdded: number;
  wordsDeleted: number;
  sentencesMoved: number;
}

export const isUnchanged = (r: TextDiffResult) => r.wordsAdded === 0 && r.wordsDeleted === 0 && r.sentencesMoved === 0;

/** `sentences(_:)`: the text split into sentences (`Sentences`, the core's one splitter), each keeping its trailing
 *  whitespace, so they rejoin exactly. */
export function sentences(text: string): string[] {
  return sentenceTexts(text);
}

const WHITESPACE = /^\p{White_Space}$/u;

/** `words(_:)`: word tokens with the whitespace after each attached. */
export function words(text: string): string[] {
  const out: string[] = [];
  let current = '';
  let inSpace = false;
  for (const c of text) {
    if (WHITESPACE.test(c)) {
      inSpace = true;
      current += c;
    } else {
      if (inSpace) {
        out.push(current);
        current = '';
        inSpace = false;
      }
      current += c;
    }
  }
  if (current) out.push(current);
  return out;
}

const trimmed = (w: string) => w.replace(/^[\p{White_Space}]+|[\p{White_Space}]+$/gu, '');
const countsAsWord = (w: string) => /[\p{L}\p{N}]/u.test(w);

/** `wordDiff`: two passages word by word (words compared without their whitespace). */
export function wordDiff(old: string, next: string): { segments: Segment[]; added: number; deleted: number } {
  const a = words(old), b = words(next);
  const changes = difference(a.map(trimmed), b.map(trimmed));
  const removed = new Set<number>(), inserted = new Set<number>();
  for (const c of changes) (c.type === 'remove' ? removed : inserted).add(c.offset);
  const segments: Segment[] = [];
  let i = 0, j = 0;
  while (i < a.length || j < b.length) {
    if (i < a.length && removed.has(i)) segments.push({ type: 'deleted', text: a[i++] });
    else if (j < b.length && inserted.has(j)) segments.push({ type: 'inserted', text: b[j++] });
    else {
      if (j < b.length) segments.push({ type: 'equal', text: b[j] });
      else if (i < a.length) segments.push({ type: 'equal', text: a[i] });
      i++;
      j++;
    }
  }
  return {
    segments,
    added: b.filter((w, k) => inserted.has(k) && countsAsWord(w)).length,
    deleted: a.filter((w, k) => removed.has(k) && countsAsWord(w)).length,
  };
}

function coalesce(segments: Segment[]): Segment[] {
  const out: Segment[] = [];
  for (const s of segments) {
    const last = out[out.length - 1];
    if (last && last.type === s.type && (s.type === 'equal' || s.type === 'inserted' || s.type === 'deleted')) {
      out[out.length - 1] = { type: s.type, text: last.text + s.text };
    } else out.push(s);
  }
  return out;
}

/** `TextDiff.diff(old:new:)` over two stored texts (the prose dialect). */
export function textDiff(old: string, next: string): TextDiffResult {
  if (old === next) return { segments: old ? [{ type: 'equal', text: old }] : [], wordsAdded: 0, wordsDeleted: 0, sentencesMoved: 0 };
  const a = sentences(plainText(old)), b = sentences(plainText(next));
  const changes = inferringMoves(difference(a, b), (s) => s);
  const removals = new Map<number, { element: string; movedTo: number | null }>();
  const insertions = new Map<number, { element: string; movedFrom: number | null }>();
  for (const c of changes) {
    if (c.type === 'remove') removals.set(c.offset, { element: c.element, movedTo: c.associatedWith });
    else insertions.set(c.offset, { element: c.element, movedFrom: c.associatedWith });
  }
  const segments: Segment[] = [];
  let added = 0, deleted = 0, moved = 0;
  let i = 0, j = 0;
  let pendingDeleted: string[] = [], pendingInserted: string[] = [];
  const flushRewrite = () => {
    if (!pendingDeleted.length && !pendingInserted.length) return;
    const w = wordDiff(pendingDeleted.join(''), pendingInserted.join(''));
    segments.push(...w.segments);
    added += w.added;
    deleted += w.deleted;
    pendingDeleted = [];
    pendingInserted = [];
  };
  while (i < a.length || j < b.length) {
    const removal = i < a.length ? removals.get(i) : undefined;
    const insertion = j < b.length ? insertions.get(j) : undefined;
    if (removal) {
      if (removal.movedTo !== null) {
        flushRewrite();
        segments.push({ type: 'movedAway', text: removal.element, toIndex: removal.movedTo });
      } else pendingDeleted.push(removal.element);
      i++;
    } else if (insertion) {
      if (insertion.movedFrom !== null) {
        flushRewrite();
        segments.push({ type: 'movedHere', text: insertion.element, fromIndex: insertion.movedFrom });
        moved++;
      } else pendingInserted.push(insertion.element);
      j++;
    } else {
      flushRewrite();
      if (i < a.length) segments.push({ type: 'equal', text: a[i] });
      i++;
      j++;
    }
  }
  flushRewrite();
  return { segments: coalesce(segments), wordsAdded: added, wordsDeleted: deleted, sentencesMoved: moved };
}
